import { HttpClient, HttpErrorResponse, type HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { EMPTY, type Observable, catchError, throwError } from 'rxjs';

import { ActivePartnerContext } from '../session/active-partner-context';
import { AUTH_CONFIG } from '../session/auth.config';
import { SessionRefreshService } from '../session/session-refresh.service';
import { SessionService } from '../session/session.service';
import { TokenRefreshCoordinator } from './token-refresh.coordinator';

/** Backend message on a hard authorization failure — no refresh, sign out. */
const ACCESS_DENIED = 'Access Denied';

/**
 * The new-standard replacement for the legacy class-based `TokenInterceptor`.
 * For requests to the configured `apiUrl` it:
 *
 * - sends cookie credentials (`withCredentials`) — the httpOnly session rides
 *   the cookie, so no `Authorization` header;
 * - attaches the active-partner context header (`creatorSub`) when one is set,
 *   so the backend `CapabilitiesGuard` resolves the right partner;
 * - on a hard `401 "Access Denied"`, signs out and redirects;
 * - on any other `401`, runs a single-flight token refresh (shared across
 *   concurrent requests) and retries; a failed refresh signs out.
 *
 * Requests to `authentication` endpoints (login / refresh / logout) opt out of
 * the 401 handling so the refresh and logout calls below don't recurse.
 *
 * This is a UI/transport concern only — the backend guards remain the real
 * enforcement.
 */
export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const { apiUrl } = inject(AUTH_CONFIG);

  // Empty `apiUrl` ⇒ the library is unconfigured; touch nothing. The guard is
  // required because `''.startsWith(anything)` is `true`.
  if (!apiUrl || !req.url.startsWith(apiUrl)) {
    return next(req);
  }

  const session = inject(SessionService);
  const activePartner = inject(ActivePartnerContext);
  const coordinator = inject(TokenRefreshCoordinator);
  const sessionRefresh = inject(SessionRefreshService);
  const http = inject(HttpClient);

  const partnerId = activePartner.activePartnerId();
  const authReq = req.clone({
    withCredentials: true,
    setHeaders: partnerId ? { creatorSub: partnerId } : {},
  });

  return next(authReq).pipe(
    catchError((error: HttpErrorResponse) => {
      // Auth endpoints handle their own failures — never refresh/sign-out here,
      // or the refresh and logout calls would recurse through this interceptor.
      // Matched as a path prefix, not a substring: a bare `includes` would also
      // opt out any URL that merely mentions the word (a query param, a longer
      // path segment), silently costing it both refresh-on-401 and sign-out.
      if (authReq.url.startsWith(`${apiUrl}/authentication/`)) {
        return throwError(() => error);
      }

      if (error.status === 401 && error.error?.message === ACCESS_DENIED) {
        return signOut(http, session, activePartner, apiUrl);
      }

      if (error.status === 401) {
        return coordinator
          .run(
            // Refresh AND adopt the new readable token (rewrite the cookie +
            // re-read the session), so the decoded session stays fresh — not
            // just the httpOnly cookies. Single-flight across the queue.
            () => sessionRefresh.refresh(),
            () => next(authReq),
          )
          .pipe(
            catchError((refreshError: unknown) => {
              // Refresh (or the retried request) is still unauthorized — the
              // session is dead, so sign out.
              if (refreshError instanceof HttpErrorResponse && refreshError.status === 401) {
                return signOut(http, session, activePartner, apiUrl);
              }
              return throwError(() => refreshError);
            }),
          );
      }

      return throwError(() => error);
    }),
  );
};

/**
 * Clear the session everywhere: best-effort server logout (clears the httpOnly
 * cookies), drop local decoded state and the active-partner choice, then
 * full-page redirect to the shared sign-in. Returns `EMPTY` so the failed
 * request quietly completes while the browser navigates away. The logout call
 * self-skips the 401 handling above (its URL contains `authentication`), and
 * its own failure is swallowed.
 */
function signOut(
  http: HttpClient,
  session: SessionService,
  activePartner: ActivePartnerContext,
  apiUrl: string,
): Observable<never> {
  http
    .post(`${apiUrl}/authentication/logout`, {}, { withCredentials: true })
    .pipe(catchError(() => EMPTY))
    .subscribe({
      complete: () => {
        session.clear();
        activePartner.clear();
        session.redirectToLogin();
      },
    });
  return EMPTY;
}
